'use client'

import Image from 'next/image'
import { useTranslations } from 'next-intl'
import { useOpenStatusLabel } from './OpenStatusBadge'
import type { OpenStatus } from '@/lib/hours/openStatus'

type Props = {
  businessName: string
  tagline?: string
  heroImageUrl?: string
  heroVideoUrl?: string
  city?: string | null
  pickupWaitMinutes?: number
  readyInLabel?: string
  onViewMenu: () => void
  onDelivery?: () => void
  hasDelivery?: boolean
  openStatus?: OpenStatus | null
}

/** Cobre de firma del tema vino (monograma, hairlines, punto central). */
const COPPER = '#c98a5e'

/** Hairline con gradiente al acento y punto cobre al centro — detalle de carta
 *  de cava del tema vino. */
function Rule({ className = '' }: { className?: string }) {
  return (
    <div className={`flex items-center gap-3 ${className}`} aria-hidden>
      <span
        className="block h-px w-12 sm:w-20"
        style={{ background: 'linear-gradient(to right, transparent, color-mix(in srgb, var(--ms-accent) 62%, transparent))' }}
      />
      <span className="block w-[4px] h-[4px] rounded-full" style={{ background: COPPER }} />
      <span
        className="block h-px w-12 sm:w-20"
        style={{ background: 'linear-gradient(to left, transparent, color-mix(in srgb, var(--ms-accent) 62%, transparent))' }}
      />
    </div>
  )
}

/**
 * Hero del tema "vino" (burdeos nocturno): chophouse con cava. Composición
 * centrada sobre burdeos casi negro con un monograma circular de doble anillo
 * en cobre (la inicial del negocio), nombre en Prata y hairlines con gradiente.
 * Si hay foto/video va a sangre bajo el overlay del token; si no, el fondo es
 * burdeos con glow tenue del acento. Lee tokens del tema (--ms-*).
 */
export function HeroVino({
  businessName,
  tagline,
  heroImageUrl,
  heroVideoUrl,
  city,
  pickupWaitMinutes,
  readyInLabel,
  onViewMenu,
  onDelivery,
  hasDelivery,
  openStatus,
}: Props) {
  const t = useTranslations('public.microsite')
  const statusLabel = useOpenStatusLabel(openStatus ?? null)
  const hasMedia = !!(heroImageUrl || heroVideoUrl)
  const monogram = businessName.trim().charAt(0).toUpperCase()

  return (
    <section className="relative w-full overflow-hidden" style={{ background: 'var(--ms-bg)' }}>
      {/* Media a sangre (opcional) */}
      {heroVideoUrl ? (
        <video autoPlay muted loop playsInline className="absolute inset-0 w-full h-full object-cover" src={heroVideoUrl} />
      ) : heroImageUrl ? (
        <Image src={heroImageUrl} alt={businessName} fill priority sizes="100vw" className="object-cover" />
      ) : null}
      {hasMedia && <div className="absolute inset-0 z-10" style={{ background: 'var(--ms-hero-overlay)' }} />}

      {/* Atmósfera: glow del acento arriba + viñeta para profundidad */}
      <div
        className="absolute inset-0 z-10 pointer-events-none"
        style={{ background: 'radial-gradient(120% 78% at 50% -12%, color-mix(in srgb, var(--ms-accent) 13%, transparent), transparent 55%)' }}
      />
      <div
        className="absolute inset-0 z-10 pointer-events-none"
        style={{ background: 'radial-gradient(80% 64% at 50% 46%, transparent 46%, rgba(8,2,5,0.6) 100%)' }}
      />

      {/* Contenido centrado */}
      <div
        className="relative z-20 mx-auto flex max-w-3xl flex-col items-center justify-center px-6 text-center"
        style={{ minHeight: 'min(680px, 88vh)' }}
      >
        {/* Monograma de doble anillo en cobre — sello de la casa */}
        {monogram && (
          <span
            className="relative flex h-[58px] w-[58px] items-center justify-center rounded-full"
            style={{
              border: `1px solid color-mix(in srgb, ${COPPER} 65%, transparent)`,
              color: COPPER,
              fontFamily: 'var(--ms-font-display)',
              fontSize: 24,
            }}
            aria-hidden
          >
            <span
              className="absolute rounded-full"
              style={{ inset: 4, border: `1px solid color-mix(in srgb, ${COPPER} 28%, transparent)` }}
            />
            {monogram}
          </span>
        )}

        {city && (
          <span className="mt-6 text-[11px] font-semibold uppercase tracking-[0.38em] pl-[0.38em]" style={{ color: 'var(--ms-accent)' }}>
            {city}
          </span>
        )}

        <h1
          className="mt-5 font-normal leading-[1.02] tracking-[0.01em] text-[clamp(46px,9vw,94px)]"
          style={{
            fontFamily: 'var(--ms-font-display)',
            color: 'var(--ms-text)',
            textShadow: hasMedia ? '0 6px 40px rgba(0,0,0,0.65)' : undefined,
          }}
        >
          {businessName}
        </h1>

        {tagline && (
          <p className="mt-4 max-w-xl text-[16px] sm:text-[18px] leading-relaxed" style={{ color: 'var(--ms-text-soft)' }}>
            {tagline}
          </p>
        )}

        <Rule className="mt-7" />

        {/* Meta discreta */}
        <div className="mt-6 flex flex-wrap items-center justify-center gap-x-4 gap-y-1 text-[12.5px] tracking-wide" style={{ color: 'var(--ms-text-muted)' }}>
          {statusLabel && (
            <span className="inline-flex items-center gap-2">
              <span
                className="w-[6px] h-[6px] rounded-full"
                style={{
                  background:
                    statusLabel.tone === 'open'
                      ? '#5fcf80'
                      : statusLabel.tone === 'soon'
                        ? '#f59e0b'
                        : 'var(--ms-text-muted)',
                }}
              />
              {statusLabel.text}
            </span>
          )}
          {statusLabel && <span aria-hidden>·</span>}
          <span>{t('pickupAvailable')}</span>
          {pickupWaitMinutes ? (
            <>
              <span aria-hidden>·</span>
              <span>{readyInLabel ?? t('readyIn', { minutes: pickupWaitMinutes })}</span>
            </>
          ) : null}
        </div>

        {/* CTAs: principal invertido (marfil sobre burdeos), delivery outline */}
        <div className="mt-9 flex flex-wrap items-center justify-center gap-3">
          <button
            type="button"
            onClick={onViewMenu}
            className="inline-flex items-center px-9 py-4 text-[13px] font-bold uppercase tracking-[0.16em] transition hover:brightness-105 active:scale-[0.98]"
            style={{
              background: 'var(--ms-text)',
              color: 'var(--ms-bg)',
              borderRadius: 'var(--ms-radius)',
              boxShadow: '0 18px 42px -18px rgba(0,0,0,0.75)',
            }}
          >
            {t('orderOnline')}
          </button>
          {hasDelivery && onDelivery && (
            <button
              type="button"
              onClick={onDelivery}
              className="inline-flex items-center px-7 py-4 text-[12.5px] font-semibold uppercase tracking-[0.16em] transition hover:bg-white/5"
              style={{
                color: 'var(--ms-text)',
                borderRadius: 'var(--ms-radius)',
                border: '1px solid color-mix(in srgb, var(--ms-accent) 45%, transparent)',
              }}
            >
              {t('delivery')}
            </button>
          )}
        </div>
      </div>
    </section>
  )
}
